import React from "react";
import type { IconButtonProps, IconButtonRef } from "./icon-button.types";
import { iconButtonStyles } from "./icon-button.styles";

export const IconButton = React.forwardRef(function IconButton<T extends React.ElementType = "button">(
  props: IconButtonProps<T>,
  ref: React.Ref<IconButtonRef<T>>,
) {
  const { children, variant = "solid", size = "m", className, as = "button" as T, disabled = false, ...rest } = props;

  const classes = iconButtonStyles({
    variant,
    size,
    disabled,
  });

  const Component = as;

  const componentProps = {
    className,
    ...rest,
  };

  // Two-step cast required: componentProps is typed as a spread of generic
  // props, so a direct cast to Record<string, unknown> is rejected by TS.
  const mutableProps = componentProps as unknown as Record<string, unknown>;
  if (as === "button" || !as) {
    mutableProps.disabled = disabled;
  } else if (as === "a" && disabled) {
    mutableProps["aria-disabled"] = true;
    mutableProps.onClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
  }

  return (
    <Component ref={ref} {...componentProps}>
      <span className={classes}>{children}</span>
    </Component>
  );
}) as (<T extends React.ElementType = "button">(
  props: IconButtonProps<T> & { ref?: React.Ref<IconButtonRef<T>> },
) => React.ReactElement) & { displayName: string };

IconButton.displayName = "IconButton";
